import { Component } from '@theme/component';
import { debounce, isClickedOutside, onAnimationEnd } from '@theme/utilities';

/**
 * The body scroll lock is one piece of global state that every dialog writes to.
 * Modals overlap routinely — adding to cart from the quick-add modal opens the cart
 * drawer while the modal is still animating out — and without an owner set the one
 * that closes last clears a lock the other still needs, unpinning the page behind an
 * open drawer. Track who holds it and only release on the way out of the last one.
 *
 * @type {Set<object>}
 */
const scrollLockOwners = new Set();

/**
 * @param {object} owner - The dialog component taking the lock.
 * @param {number} scrollY - The offset to pin the body at.
 */
function lockBodyScroll(owner, scrollY) {
  if (scrollLockOwners.size === 0) {
    document.body.style.width = '100%';
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollY}px`;
  }

  scrollLockOwners.add(owner);
}

/**
 * @param {object} owner - The dialog component giving the lock up.
 * @param {number} scrollY - Offset to restore. Only the last owner out gets to apply it.
 */
function releaseBodyScroll(owner, scrollY) {
  if (!scrollLockOwners.delete(owner)) return;

  if (scrollLockOwners.size > 0) return;

  document.body.style.width = '';
  document.body.style.position = '';
  document.body.style.top = '';

  /*
   * Reading a layout property forces the document to take its full scroll range back before we
   * ask for an offset inside it. While the body is pinned the page has almost no scrollable
   * height, and Safari clamps a scrollTo against the height it currently knows about rather
   * than the one it is about to have -- so the restore silently became a jump to the top.
   */
  void document.body.offsetHeight;

  scrollWithoutAnimation(scrollY);
}

/**
 * Scrolls the window with the smooth behaviour <html> asks for suppressed, so a restore lands in
 * one go instead of animating. `behavior: 'instant'` says the same thing, but older Safari rejects
 * that enum value and throws rather than scrolling, which loses the restore altogether.
 *
 * @param {number} scrollY - Offset to scroll to.
 */
export function scrollWithoutAnimation(scrollY) {
  const { scrollBehavior } = document.documentElement.style;

  document.documentElement.style.scrollBehavior = 'auto';
  window.scrollTo(0, scrollY);
  document.documentElement.style.scrollBehavior = scrollBehavior;
}

/**
 * A custom element that manages a dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog – The dialog element.
 *
 * @extends Component<Refs>
 */
export class DialogComponent extends Component {
  requiredRefs = ['dialog'];

  connectedCallback() {
    super.connectedCallback();

    if (this.minWidth || this.maxWidth) {
      window.addEventListener('resize', this.#handleResize);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.minWidth || this.maxWidth) {
      window.removeEventListener('resize', this.#handleResize);
    }

    releaseBodyScroll(this, this.#previousScrollY);
  }

  #handleResize = debounce(() => {
    const { minWidth, maxWidth } = this;

    if (!minWidth && !maxWidth) return;

    const windowWidth = window.innerWidth;
    if (windowWidth < minWidth || windowWidth > maxWidth) {
      this.closeDialog();
    }
  }, 50);

  #previousScrollY = 0;

  showDialog() {
    const { dialog } = this.refs;

    if (dialog.open) return;

    const lockedTop = document.body.style.position === 'fixed' ? document.body.style.top : '';
    const scrollY = lockedTop ? Math.abs(parseInt(lockedTop, 10)) || 0 : window.scrollY;

    this.#previousScrollY = scrollY;

    requestAnimationFrame(() => {
      if (dialog.open) return;

      lockBodyScroll(this, scrollY);

      dialog.showModal();
      this.dispatchEvent(new DialogOpenEvent());

      this.addEventListener('click', this.#handleClick);
      this.addEventListener('keydown', this.#handleKeyDown);
    });
  }

  closeDialog = async () => {
    const { dialog } = this.refs;

    if (!dialog.open && !dialog.matches(':modal')) return;

    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('keydown', this.#handleKeyDown);

    dialog.style.animation = 'none';

    void dialog.offsetWidth;

    dialog.classList.add('dialog-closing');
    dialog.style.animation = '';

    await onAnimationEnd(dialog, undefined, {
      subtree: false,
    });

    /*
     * The dialog has to leave the top layer before the scroll is restored, not after. A modal
     * <dialog> is still holding the browser's own scroll lock while it is open, and Safari
     * refuses the scrollTo underneath it -- so the old order unpinned the body, lost the
     * restore to that refusal, and only then closed the dialog, leaving the page at the top.
     */
    if (!dialog.open && dialog.matches(':modal')) dialog.setAttribute('open', '');
    if (dialog.open) dialog.close();
    dialog.classList.remove('dialog-closing');

    releaseBodyScroll(this, this.#previousScrollY);

    this.dispatchEvent(new DialogCloseEvent());
  };

  surrenderScrollLock() {
    scrollLockOwners.delete(this);
  }

  toggleDialog = () => {
    if (this.refs.dialog.open) {
      this.closeDialog();
    } else {
      this.showDialog();
    }
  };

  /**
   * Closes the dialog when the user clicks outside of it.
   *
   * @param {MouseEvent} event - The mouse event.
   */
  #handleClick(event) {
    const { dialog } = this.refs;

    if (isClickedOutside(event, dialog)) {
      this.closeDialog();
    }
  }

  /**
   * Closes the dialog when the user presses the escape key.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown(event) {
    if (event.key !== 'Escape') return;

    event.preventDefault();
    this.closeDialog();
  }

  /**
   * Gets the minimum width of the dialog.
   *
   * @returns {number} The minimum width of the dialog.
   */
  get minWidth() {
    return Number(this.getAttribute('dialog-active-min-width'));
  }

  /**
   * Gets the maximum width of the dialog.
   *
   * @returns {number} The maximum width of the dialog.
   */
  get maxWidth() {
    return Number(this.getAttribute('dialog-active-max-width'));
  }
}

if (!customElements.get('dialog-component')) customElements.define('dialog-component', DialogComponent);

export class DialogOpenEvent extends CustomEvent {
  constructor() {
    super(DialogOpenEvent.eventName);
  }

  static eventName = 'dialog:open';
}

export class DialogCloseEvent extends CustomEvent {
  constructor() {
    super(DialogCloseEvent.eventName);
  }

  static eventName = 'dialog:close';
}

document.addEventListener(
  'toggle',
  (event) => {
    if (event.target instanceof HTMLDetailsElement) {
      if (event.target.hasAttribute('scroll-lock')) {
        const { open } = event.target;
        if (open) {
          document.documentElement.setAttribute('scroll-lock', '');
        } else {
          document.documentElement.removeAttribute('scroll-lock');
        }
      }
    }
  },
  { capture: true }
);
